import { useEffect, useMemo, useState } from "react";
import { LeadAPI } from "api/lead/service";
import {
  Check,
  Send,
  ChevronDown,
  ChevronRight,
  Clock,
  MessageSquare,
  Edit3,
  Image as ImageIcon,
  Trash2,
  Plus,
  MoreVertical,
  Pencil,
  ArrowRight,
  Eye,
} from "lucide-react";
import { AddRequirementForm } from "./AddRequirementForm";

function formatDateOnly(isoLike) {
  if (!isoLike) return "";
  return String(isoLike).split("T")[0];
}

export function ScopeOfWorkView({ client, onCreateQuotation, canApprove = false, canViewQuotation = true }) {
  const [scope, setScope] = useState(null);
  const [isLoadingScope, setIsLoadingScope] = useState(false);
  const [scopeError, setScopeError] = useState("");
  const [scopeActionError, setScopeActionError] = useState("");
  const [isWorkingOnScopeAction, setIsWorkingOnScopeAction] = useState(false);
  const [isProceedingToQuotation, setIsProceedingToQuotation] = useState(false);
  const [isClientIssueFlow, setIsClientIssueFlow] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(["c1"]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [scopeVersions, setScopeVersions] = useState([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [versionsError, setVersionsError] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [versionDetail, setVersionDetail] = useState(null);
  const [isLoadingVersionDetail, setIsLoadingVersionDetail] = useState(false);
  const [versionDetailError, setVersionDetailError] = useState("");
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [addingRequirementFor, setAddingRequirementFor] = useState(null);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState("");
  const [isCreatingRequirement, setIsCreatingRequirement] = useState(false);
  const [requirementError, setRequirementError] = useState("");
  const [feedback, setFeedback] = useState([]);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [newFeedbackMessage, setNewFeedbackMessage] = useState("");
  const [isCreatingFeedback, setIsCreatingFeedback] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // { type: "category"|"feedback", id: string }
  const [editCategoryModal, setEditCategoryModal] = useState(null); // { id, name }
  const [editCategoryName, setEditCategoryName] = useState("");
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [editRequirementModal, setEditRequirementModal] = useState(null); // { id, categoryId, field, type, value }
  const [editRequirement, setEditRequirement] = useState({ field: "", type: "", value: "" });
  const [isSavingRequirement, setIsSavingRequirement] = useState(false);
  const [editFeedbackModal, setEditFeedbackModal] = useState(null); // { id, message }
  const [editFeedbackMessage, setEditFeedbackMessage] = useState("");
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);

  function normalizeScope(raw) {
    if (!raw) return null;
    const payload = raw?.data && typeof raw?.success !== "undefined" ? raw.data : raw;
    const statusRaw = String(payload.status || "").trim().toLowerCase();
    const status =
      statusRaw === "draft"
        ? "Draft"
        : statusRaw === "sent"
          ? "Sent"
          : statusRaw === "approver_approved"
            ? "Approver Approved"
            : statusRaw === "client_approved"
              ? "Client Approved"
              : statusRaw === "approved"
                ? "Approved"
                : statusRaw === "rejected"
                  ? "Rejected"
                  : (payload.status || "Draft");

    const tentativeStart =
      payload.tentative_start ||
      payload.tentativeStart ||
      payload.start_date ||
      payload.startDate ||
      "";

    return {
      id: String(payload.id ?? payload.pk ?? ""),
      name: payload.name || "Scope of Work",
      projectType: payload.project_type || payload.projectType || client?.projectType || "Interior",
      propertySize: payload.property_size || payload.propertySize || client?.propertySize || "",
      budget: payload.budget ?? "",
      tentativeStart: tentativeStart ? String(tentativeStart).split("T")[0] : "",
      status,
      statusRaw,
      categories: (payload.categories || []).map((c) => ({
        ...c,
        requirements: Array.isArray(c.requirements) ? c.requirements : [],
      })),
      versions: payload.versions || [],
      currentVersion: payload.current_version ?? payload.currentVersion,
    };
  }

  const refreshScope = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    const res = await LeadAPI.getScopeFull(orgId, leadId);
    const mapped = normalizeScope(res);
    setScope(mapped);
  };

  const normalizedFeedback = useMemo(() => {
    const list = Array.isArray(feedback) ? feedback : [];
    return list
      .map((f) => ({
        id: String(f?.id ?? f?.pk ?? ""),
        message: f?.message || f?.text || "",
        date: formatDateOnly(f?.created_on || f?.createdAt || f?.created_at || f?.date || ""),
        by: f?.created_by || f?.updated_by || "",
        isClient:
          typeof f?.is_client === "boolean"
            ? f.is_client
            : (f?.by ? String(f.by).toLowerCase().includes("client") : false),
      }))
      .filter((f) => f.id || f.message);
  }, [feedback]);

  const versionSnapshotCategories = useMemo(() => {
    const snapshot = versionDetail?.snapshot_data || versionDetail?.snapshot || {};
    const cats = Array.isArray(snapshot?.categories) ? snapshot.categories : [];
    return cats.map((category) => ({
      ...category,
      requirements: Array.isArray(category?.requirements) ? category.requirements : [],
    }));
  }, [versionDetail]);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    let cancelled = false;
    (async () => {
      setScopeError("");
      setIsLoadingScope(true);
      try {
        const res = await LeadAPI.getScopeFull(orgId, leadId);
        const mapped = normalizeScope(res);
        if (!cancelled) setScope(mapped);
      } catch (e) {
        // If scope doesn't exist yet, show empty state
        if (!cancelled) {
          const status = e?.status;
          if (status === 404) setScope(null);
          else {
            setScope(null);
            setScopeError(e?.message || "Failed to load scope");
          }
        }
      } finally {
        if (!cancelled) setIsLoadingScope(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client?.id]);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scope?.id;
    if (!orgId || !leadId || !scopeId) return;

    let cancelled = false;
    (async () => {
      setFeedbackError("");
      setIsLoadingFeedback(true);
      try {
        const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
        const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
        if (!cancelled) setFeedback(list || []);
      } catch (e) {
        if (!cancelled) setFeedbackError(e?.message || "Failed to load client feedback");
      } finally {
        if (!cancelled) setIsLoadingFeedback(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client?.id, scope?.id]);

  const handleCreateFeedback = async () => {
    const message = String(newFeedbackMessage || "").trim();
    if (!message) return;

    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scope?.id;
    if (!orgId || !leadId || !scopeId) {
      setFeedbackError("Organization/Lead/Scope not found");
      return;
    }

    setIsCreatingFeedback(true);
    setFeedbackError("");
    try {
      await LeadAPI.createScopeFeedback(orgId, leadId, scopeId, {
        scope: Number(scopeId),
        message,
      });
      if (isClientIssueFlow) {
        await LeadAPI.reopenScope(orgId, leadId);
        await refreshScope();
        setIsClientIssueFlow(false);
      }
      setIsFeedbackModalOpen(false);
      setNewFeedbackMessage("");

      // Re-fetch to ensure refresh correctness
      const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
      const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setFeedback(list || []);
    } catch (e) {
      setFeedbackError(e?.message || "Failed to save feedback");
    } finally {
      setIsCreatingFeedback(false);
    }
  };

  const openCategoryEdit = (cat) => {
    setCategoryError("");
    setEditCategoryModal({ id: String(cat.id), name: cat.name || "" });
    setEditCategoryName(cat.name || "");
  };

  const saveCategoryEdit = async () => {
    const name = String(editCategoryName || "").trim();
    if (!name || !editCategoryModal) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setIsSavingCategory(true);
    setCategoryError("");
    try {
      const categoryId = editCategoryModal.id;
      setScope((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categories: (prev.categories || []).map((c) =>
            String(c.id) === String(categoryId) ? { ...c, name } : c
          ),
        };
      });
      await LeadAPI.updateScopeCategory(orgId, leadId, categoryId, { name });
      await refreshScope();
      setEditCategoryModal(null);
    } catch (e) {
      setCategoryError(e?.message || "Failed to update category");
      await refreshScope().catch(() => { });
    } finally {
      setIsSavingCategory(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    try {
      setScope((prev) => {
        if (!prev) return prev;
        return { ...prev, categories: (prev.categories || []).filter((c) => String(c.id) !== String(categoryId)) };
      });
      await LeadAPI.deleteScopeCategory(orgId, leadId, categoryId);
      await refreshScope();
    } catch (_) {
      await refreshScope().catch(() => { });
    }
  };

  const openRequirementEdit = (categoryId, req) => {
    setRequirementError("");
    setEditRequirementModal({
      id: String(req.id),
      categoryId: String(categoryId),
      field: req.field || "",
      type: req.type || "",
      value: req.value || "",
    });
    setEditRequirement({ field: req.field || "", type: req.type || "", value: req.value || "" });
  };

  const saveRequirementEdit = async () => {
    if (!editRequirementModal) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    const payload = {
      field: String(editRequirement.field || "").trim(),
      type: String(editRequirement.type || "").trim(),
      value: String(editRequirement.value || "").trim(),
    };
    if (!payload.field || !payload.type) return;

    setIsSavingRequirement(true);
    setRequirementError("");
    try {
      const reqId = editRequirementModal.id;
      setScope((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categories: (prev.categories || []).map((c) => ({
            ...c,
            requirements: (c.requirements || []).map((r) =>
              String(r.id) === String(reqId) ? { ...r, ...payload } : r
            ),
          })),
        };
      });
      await LeadAPI.updateRequirement(orgId, leadId, reqId, payload);
      await refreshScope();
      setEditRequirementModal(null);
    } catch (e) {
      setRequirementError(e?.message || "Failed to update requirement");
      await refreshScope().catch(() => { });
    } finally {
      setIsSavingRequirement(false);
    }
  };

  const deleteRequirement = async (reqId) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    try {
      setScope((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categories: (prev.categories || []).map((c) => ({
            ...c,
            requirements: (c.requirements || []).filter((r) => String(r.id) !== String(reqId)),
          })),
        };
      });
      await LeadAPI.deleteRequirement(orgId, leadId, reqId);
      await refreshScope();
    } catch (_) {
      await refreshScope().catch(() => { });
    }
  };

  const openRequirementImage = (req) => {
    const list = Array.isArray(req.images) ? req.images : [];
    const image = list.find((img) => img.image_url || img.image);
    if (!image) return;
    const url = image.image_url || image.image;
    if (!url) return;
    window.open(url, "_blank");
  };

  const openFeedbackEdit = (fb) => {
    setFeedbackError("");
    setEditFeedbackModal({ id: String(fb.id), message: fb.message || "" });
    setEditFeedbackMessage(fb.message || "");
  };

  const saveFeedbackEdit = async () => {
    const message = String(editFeedbackMessage || "").trim();
    if (!message || !editFeedbackModal) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scope?.id;
    if (!orgId || !leadId || !scopeId) return;
    setIsSavingFeedback(true);
    setFeedbackError("");
    try {
      const id = editFeedbackModal.id;
      setFeedback((prev) =>
        (Array.isArray(prev) ? prev : []).map((x) => (String(x.id) === String(id) ? { ...x, message } : x))
      );
      await LeadAPI.updateScopeFeedback(orgId, leadId, scopeId, id, { message });
      const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
      const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setFeedback(list || []);
      setEditFeedbackModal(null);
    } catch (e) {
      setFeedbackError(e?.message || "Failed to update feedback");
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const deleteFeedback = async (fbId) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scope?.id;
    if (!orgId || !leadId || !scopeId) return;
    try {
      setFeedback((prev) => (Array.isArray(prev) ? prev : []).filter((x) => String(x.id) !== String(fbId)));
      await LeadAPI.deleteScopeFeedback(orgId, leadId, scopeId, fbId);
      const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
      const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setFeedback(list || []);
    } catch (_) {
      const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
      const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setFeedback(list || []);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleAddRequirement = (categoryId, requirement) => {
    (async () => {
      if (!scope) return;
      const orgId = LeadAPI.orgId();
      const leadId = client?.id;
      if (!orgId || !leadId) {
        setRequirementError("Organization/Lead not found");
        return;
      }

      setIsCreatingRequirement(true);
      setRequirementError("");
      try {
        const payload = {
          field: requirement.field,
          type: requirement.type,
          value: requirement.value,
        };
        const res = await LeadAPI.createRequirement(orgId, leadId, categoryId, payload);
        const created = res?.data || res;
        const requirementId = created?.id ?? created?.pk;

        const files = Array.isArray(requirement.imageFiles) ? requirement.imageFiles : [];
        // Upload each image sequentially (UI allows up to 2)
        for (const f of files) {
          await LeadAPI.uploadRequirementImage(orgId, leadId, requirementId, f);
        }

        await refreshScope();
        setAddingRequirementFor(null);
      } catch (e) {
        setRequirementError(e?.message || "Failed to add requirement");
      } finally {
        setIsCreatingRequirement(false);
      }
    })();
  };

  const handleCreateCategory = async () => {
    const name = String(newCategoryName || "").trim();
    if (!name) return;
    if (!scope) return;

    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) {
      setCategoryError("Organization/Lead not found");
      return;
    }

    setIsCreatingCategory(true);
    setCategoryError("");
    try {
      const res = await LeadAPI.createScopeCategory(orgId, leadId, { name });
      const created = res?.data || res;
      const categoryId = created?.id ?? created?.pk;

      let full = created;
      if (categoryId != null) {
        // Fetch full category shape (some APIs return only id+name on create)
        const fetched = await LeadAPI.getScopeCategory(orgId, leadId, categoryId);
        full = fetched?.data || fetched;
      }

      const normalized = {
        id: String(full?.id ?? full?.pk ?? categoryId ?? Date.now()),
        name: full?.name || name,
        requirements: Array.isArray(full?.requirements) ? full.requirements : [],
      };

      setScope((prev) => {
        if (!prev) return prev;
        const existing = Array.isArray(prev.categories) ? prev.categories : [];
        const deduped = existing.filter((c) => String(c.id) !== String(normalized.id));
        return { ...prev, categories: [normalized, ...deduped] };
      });
      setExpandedCategories((prev) => {
        const id = normalized.id;
        return prev.includes(id) ? prev : [id, ...prev];
      });
      setIsAddCategoryOpen(false);
      setNewCategoryName("");
    } catch (e) {
      setCategoryError(e?.message || "Failed to add category");
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      Draft: "bg-gray-200 text-gray-700",
      Sent: "bg-blue-100 text-blue-700",
      "Sent for Approval": "bg-blue-100 text-blue-700",
      Open: "bg-gray-200 text-gray-700",
      "Approver Approved": "bg-orange-100 text-orange-700",
      "Client Approved": "bg-green-100 text-green-700",
      Approved: "bg-green-100 text-green-700",
      Rejected: "bg-red-100 text-red-700",
    };
    return (
      <span
        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${styles[status]}`}
      >
        {status}
      </span>
    );
  };

  const statusRaw = String(scope?.statusRaw || "").toLowerCase();
  const isLockedForEdits = ["sent", "approver_approved", "client_approved"].includes(statusRaw);
  const isClientApproved = statusRaw === "client_approved";
  const canEditRequirements = !isLockedForEdits;

  const statusTagForHeader = () => {
    if (canApprove) {
      if (statusRaw === "draft") return "Open";
      if (statusRaw === "sent") return "Sent for Approval";
      if (statusRaw === "approver_approved") return "Approver Approved";
      if (statusRaw === "client_approved") return "Client Approved";
      return scope?.status || "Open";
    }
    // non-approver
    if (statusRaw === "sent") return "Sent for Approval";
    if (statusRaw === "approver_approved") return "Approver Approved";
    if (statusRaw === "client_approved") return "Client Approved";
    return scope?.status || "Draft";
  };

  const sendForApproval = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.sendScopeForApproval(orgId, leadId);
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to send for approval");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const selfApproveAndSendToClient = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.updateScope(orgId, leadId, { status: "approver_approved" });
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to approve scope");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const approveAsApprover = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.approveScopeAsApprover(orgId, leadId);
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to approve scope");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const rejectAsApprover = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.rejectScopeAsApprover(orgId, leadId);
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to reject scope");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const approveAsClient = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.approveScopeAsClient(orgId, leadId);
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to mark client approved");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const openClientIssueModal = () => {
    setFeedbackError("");
    setNewFeedbackMessage("");
    setIsClientIssueFlow(true);
    setIsFeedbackModalOpen(true);
  };

  const proceedToQuotation = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId || !scope?.id) return;
    setScopeActionError("");
    setIsProceedingToQuotation(true);
    try {
      // If quotation exists, just navigate
      try {
        await LeadAPI.getQuotation(orgId, leadId);
      } catch (e) {
        if (e?.status === 404) {
          await LeadAPI.createQuotation(orgId, leadId, {
            scope: Number(scope.id),
            name: `Quotation for ${scope.name}`,
            payment_terms: "",
            terms_conditions: "",
          });
        } else {
          throw e;
        }
      }
      onCreateQuotation(scope.id);
    } catch (e) {
      setScopeActionError(e?.message || "Failed to proceed to quotation");
    } finally {
      setIsProceedingToQuotation(false);
    }
  };

  const loadVersions = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setVersionsError("");
    setIsLoadingVersions(true);
    try {
      const res = await LeadAPI.listScopeVersions(orgId, leadId);
      const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setScopeVersions(Array.isArray(list) ? list : []);
    } catch (e) {
      setVersionsError(e?.message || "Failed to load version history");
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const loadVersionDetail = async (versionId) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId || !versionId) return;
    setVersionDetailError("");
    setIsLoadingVersionDetail(true);
    try {
      const res = await LeadAPI.getScopeVersion(orgId, leadId, versionId);
      const data = res?.data && typeof res?.success !== "undefined" ? res.data : res;
      setVersionDetail(data);
    } catch (e) {
      setVersionDetailError(e?.message || "Failed to load version detail");
    } finally {
      setIsLoadingVersionDetail(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(`scopeVersionId_${client?.id || "none"}`);
    if (saved) {
      const parsed = Number(saved);
      if (!Number.isNaN(parsed)) {
        setSelectedVersionId(parsed);
        loadVersionDetail(parsed);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  useEffect(() => {
    if (selectedVersionId) {
      localStorage.setItem(`scopeVersionId_${client?.id || "none"}`, String(selectedVersionId));
    } else {
      localStorage.removeItem(`scopeVersionId_${client?.id || "none"}`);
    }
  }, [selectedVersionId, client?.id]);

  // If no scope exists yet, show empty state
  if (!scope) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <MessageSquare className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          No Scope Created Yet
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Create a scope of work after discussions and meetings are completed.
        </p>
        {scopeError ? (
          <p className="text-xs text-red-600 mb-2">{scopeError}</p>
        ) : null}
        {isLoadingScope ? (
          <p className="text-xs text-gray-400 mb-2">Loading scope...</p>
        ) : null}
        <p className="text-xs text-gray-400">
          Use the "Create Scope" button from the Lead View to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Full Width */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-0.5">
            <span>{client.name}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-900 font-medium">Scope of Work</span>
          </div>
          <h1 className="text-lg font-bold text-gray-900">{scope.name}</h1>
        </div>
        <div className="flex gap-1.5">
          {/* Non-approver workflow */}
          {(!canApprove && (statusRaw === "draft" || statusRaw === "rejected")) ? (
            <button
              className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              disabled={isWorkingOnScopeAction}
              onClick={sendForApproval}
            >
              <Send className="w-3 h-3" />
              {isWorkingOnScopeAction ? "Sending..." : "Send for Approval"}
            </button>
          ) : null}

          {(canApprove && (statusRaw === "draft" || statusRaw === "rejected")) ? (
            <button
              className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              disabled={isWorkingOnScopeAction}
              onClick={selfApproveAndSendToClient}
            >
              <Send className="w-3 h-3" />
              {isWorkingOnScopeAction ? "Sending..." : "Approve & Send to client"}
            </button>
          ) : null}

          {/* Approver workflow */}
          {canApprove && statusRaw === "sent" ? (
            <>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnScopeAction}
                onClick={approveAsApprover}
              >
                <Check className="w-3 h-3" />
                {isWorkingOnScopeAction ? "Working..." : "Approve Scope"}
              </button>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-red-300 text-red-700 rounded-md transition-colors ${isWorkingOnScopeAction ? "opacity-60 cursor-not-allowed" : "hover:bg-red-50"
                  }`}
                disabled={isWorkingOnScopeAction}
                onClick={rejectAsApprover}
              >
                Reject Scope
              </button>
            </>
          ) : null}

          {canApprove && statusRaw === "approver_approved" ? (
            <>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnScopeAction}
                onClick={approveAsClient}
              >
                <Check className="w-3 h-3" />
                Client Approved
              </button>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnScopeAction}
                onClick={openClientIssueModal}
              >
                <MessageSquare className="w-3 h-3" />
                Client Issue
              </button>
            </>
          ) : null}

          {canApprove && isClientApproved ? (
            <button
              className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium text-white rounded-md transition-colors ${isProceedingToQuotation ? "bg-gray-300 cursor-not-allowed" : "bg-sm-primary hover:bg-sm-hover"
                }`}
              disabled={isProceedingToQuotation}
              onClick={proceedToQuotation}
            >
              <ArrowRight className="w-3 h-3" />
              {isProceedingToQuotation ? "Opening..." : "Proceed to Quotation"}
            </button>
          ) : null}
        </div>
      </div>

      {/* Full Width Scope Details */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white rounded-md border border-gray-200 p-4">
          {scopeActionError ? (
            <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
              {scopeActionError}
            </div>
          ) : null}
          {/* Scope Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              {getStatusBadge(statusTagForHeader())}
              <span className="text-xs text-gray-500">
                Version {(scope.versions || []).length}
              </span>
            </div>
            {canApprove && statusRaw === "client_approved" ? null : null}
          </div>

          {/* Project Metadata - Compact Grid */}
          <div className="grid grid-cols-5 gap-3 p-3 bg-gray-50 rounded-md mb-4">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Type
              </p>
              <p className="text-xs font-medium">{scope.projectType}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Size
              </p>
              <p className="text-xs font-medium">{scope.propertySize}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Budget
              </p>
              <p className="text-xs font-medium">{scope.budget}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Start
              </p>
              <p className="text-xs font-medium">{scope.tentativeStart}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Status
              </p>
              <p className="text-xs font-medium">{scope.status}</p>
            </div>
          </div>

          {/* Categories */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                Requirements
              </h3>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${canEditRequirements ? "hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
                  }`}
                disabled={!canEditRequirements}
                onClick={() => {
                  if (!canEditRequirements) return;
                  setCategoryError("");
                  setNewCategoryName("");
                  setIsAddCategoryOpen(true);
                }}
              >
                <Plus className="w-3 h-3" />
                Add Category
              </button>
            </div>

            {requirementError ? (
              <div className="mb-2 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                {requirementError}
              </div>
            ) : null}

            {/* Add Category Modal */}
            {isAddCategoryOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">Add Category</h3>
                    <button
                      onClick={() => setIsAddCategoryOpen(false)}
                      className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                      disabled={isCreatingCategory}
                    >
                      ×
                    </button>
                  </div>
                  <div className="p-4 space-y-3">
                    {categoryError ? (
                      <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                        {categoryError}
                      </div>
                    ) : null}
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Category name *</label>
                      <input
                        type="text"
                        placeholder="Living Room"
                        className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        disabled={isCreatingCategory}
                      />
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 flex justify-end gap-2">
                    <button
                      className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                      onClick={() => setIsAddCategoryOpen(false)}
                      disabled={isCreatingCategory}
                    >
                      Cancel
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isCreatingCategory || !newCategoryName.trim()
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-sm-primary hover:bg-sm-hover"
                        }`}
                      onClick={handleCreateCategory}
                      disabled={isCreatingCategory || !newCategoryName.trim()}
                    >
                      {isCreatingCategory ? "Adding..." : "Add"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              {scope.categories.map((category) => (
                <div key={category.id} className="border border-gray-200 rounded overflow-visible">
                  <button
                    className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center gap-1.5">
                      {expandedCategories.includes(category.id) ? (
                        <ChevronDown className="w-3.5 h-3.5" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5" />
                      )}
                      <span className="text-sm font-medium">
                        {category.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500">
                        {(category.requirements || []).length} fields
                      </span>
                      <div className="relative">
                        <button
                          className={`h-6 w-6 flex items-center justify-center rounded-md transition-colors ${canEditRequirements ? "hover:bg-gray-200" : "opacity-50 cursor-not-allowed"
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canEditRequirements) return;
                            setOpenMenu((prev) =>
                              prev?.type === "category" && prev?.id === String(category.id)
                                ? null
                                : { type: "category", id: String(category.id) }
                            );
                          }}
                          disabled={!canEditRequirements}
                        >
                          <MoreVertical className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        {openMenu?.type === "category" && openMenu?.id === String(category.id) ? (
                          <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-10">
                            <button
                              className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenu(null);
                                openCategoryEdit(category);
                              }}
                            >
                              <Pencil className="w-3.5 h-3.5 text-gray-500" />
                              Edit
                            </button>
                            <button
                              className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenu(null);
                                deleteCategory(category.id);
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </button>
                  {expandedCategories.includes(category.id) && (
                    <div className="border-t border-gray-200">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                              Field
                            </th>
                            <th className="text-left text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                              Type
                            </th>
                            <th className="text-left text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                              Value
                            </th>
                            <th className="text-center text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide w-12">
                              Photo
                            </th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(category.requirements || []).map((req) => (
                            <tr key={req.id} className="border-t border-gray-200">
                              <td className="px-2.5 py-1.5 text-xs">
                                {req.field}
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-gray-500">
                                {req.type}
                              </td>
                              <td className="px-2.5 py-1.5 text-xs">
                                {req.value}
                              </td>
                              <td className="px-2.5 py-1.5 text-center">
                                {(req.images_count > 0 || (Array.isArray(req.images) && req.images.length > 0)) ? (
                                  <button
                                    type="button"
                                    className="mx-auto flex items-center justify-center"
                                    onClick={() => openRequirementImage(req)}
                                  >
                                    <ImageIcon className="w-3.5 h-3.5 text-sm-primary" />
                                  </button>
                                ) : (
                                  <span className="text-gray-400 text-xs">
                                    -
                                  </span>
                                )}
                              </td>
                              <td className="px-1">
                                <div className="flex items-center gap-1">
                                  <button
                                    className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                    onClick={() => openRequirementEdit(category.id, req)}
                                    title="Edit"
                                    disabled={!canEditRequirements}
                                  >
                                    <Pencil className="w-3 h-3 text-gray-500" />
                                  </button>
                                  <button
                                    className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                    onClick={() => deleteRequirement(req.id)}
                                    title="Delete"
                                    disabled={!canEditRequirements}
                                  >
                                    <Trash2 className="w-3 h-3 text-gray-400" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="p-2 border-t border-gray-200">
                        {addingRequirementFor === category.id && canEditRequirements ? (
                          <AddRequirementForm
                            onSave={(req) =>
                              handleAddRequirement(category.id, req)
                            }
                            onCancel={() => setAddingRequirementFor(null)}
                          />
                        ) : (
                          <button
                            className={`w-full h-7 flex items-center justify-center gap-1.5 text-xs transition-colors ${canEditRequirements ? "text-gray-500 hover:text-sm-primary" : "text-gray-400 cursor-not-allowed"
                              }`}
                            onClick={() => {
                              if (!canEditRequirements) return;
                              setRequirementError("");
                              setAddingRequirementFor(category.id);
                            }}
                            disabled={!canEditRequirements}
                          >
                            <Plus className="w-3 h-3" />
                            Add Requirement
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Client Feedback - Compact */}
          <div className="mb-4 p-2.5 bg-sm-accent/10 border border-sm-accent/30 rounded">
            <div className="flex items-center gap-1.5 mb-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-sm-accent" />
              <h4 className="text-xs font-medium text-sm-accent">
                Client Feedback
              </h4>
              <button
                className="ml-auto h-6 px-2 flex items-center gap-1 text-[10px] font-medium border border-sm-accent/30 rounded hover:bg-sm-accent/10 transition-colors"
                onClick={() => {
                  setFeedbackError("");
                  setNewFeedbackMessage("");
                  setIsFeedbackModalOpen(true);
                }}
              >
                <Edit3 className="w-3 h-3" />
                Add
              </button>
            </div>

            {feedbackError ? (
              <div className="text-[10px] text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5 mb-2">
                {feedbackError}
              </div>
            ) : null}

            {isLoadingFeedback ? (
              <div className="text-xs text-gray-500">Loading feedback...</div>
            ) : null}

            {!isLoadingFeedback && normalizedFeedback.length === 0 ? (
              <div className="text-xs text-gray-600">No feedback yet.</div>
            ) : null}

            {normalizedFeedback.map((fb) => (
              <div key={fb.id || fb.message} className="text-xs flex items-start justify-between gap-2">
                <div>
                  <p className="text-gray-900">{fb.message}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {fb.date}
                    {fb.by ? ` • ${fb.by}` : ""}
                  </p>
                </div>
                <div className="relative">
                  <button
                    className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-sm-accent/10 transition-colors"
                    onClick={() =>
                      setOpenMenu((prev) =>
                        prev?.type === "feedback" && prev?.id === String(fb.id)
                          ? null
                          : { type: "feedback", id: String(fb.id) }
                      )
                    }
                  >
                    <MoreVertical className="w-3.5 h-3.5 text-sm-accent" />
                  </button>
                  {openMenu?.type === "feedback" && openMenu?.id === String(fb.id) ? (
                    <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-10">
                      <button
                        className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => {
                          setOpenMenu(null);
                          openFeedbackEdit(fb);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5 text-gray-500" />
                        Edit
                      </button>
                      <button
                        className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                        onClick={() => {
                          setOpenMenu(null);
                          deleteFeedback(fb.id);
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {/* Edit Category Modal */}
          {editCategoryModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Edit Category</h3>
                  <button
                    onClick={() => setEditCategoryModal(null)}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isSavingCategory}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">Name *</label>
                    <input
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={editCategoryName}
                      onChange={(e) => setEditCategoryName(e.target.value)}
                      disabled={isSavingCategory}
                    />
                  </div>
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => setEditCategoryModal(null)}
                    disabled={isSavingCategory}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isSavingCategory || !editCategoryName.trim()
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={saveCategoryEdit}
                    disabled={isSavingCategory || !editCategoryName.trim()}
                  >
                    {isSavingCategory ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Requirement Modal */}
          {editRequirementModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Edit Requirement</h3>
                  <button
                    onClick={() => setEditRequirementModal(null)}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isSavingRequirement}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">Field *</label>
                    <input
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={editRequirement.field}
                      onChange={(e) => setEditRequirement({ ...editRequirement, field: e.target.value })}
                      disabled={isSavingRequirement}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Type *</label>
                      <select
                        className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                        value={editRequirement.type}
                        onChange={(e) => setEditRequirement({ ...editRequirement, type: e.target.value })}
                        disabled={isSavingRequirement}
                      >
                        <option value="">Select type</option>
                        <option value="Boolean">Boolean</option>
                        <option value="Text">Text</option>
                        <option value="Dimensions">Dimensions</option>
                        <option value="Number">Number</option>
                        <option value="Color">Color</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Value</label>
                      <input
                        className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                        value={editRequirement.value}
                        onChange={(e) => setEditRequirement({ ...editRequirement, value: e.target.value })}
                        disabled={isSavingRequirement}
                      />
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => setEditRequirementModal(null)}
                    disabled={isSavingRequirement}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isSavingRequirement || !String(editRequirement.field || "").trim() || !String(editRequirement.type || "").trim()
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={saveRequirementEdit}
                    disabled={
                      isSavingRequirement ||
                      !String(editRequirement.field || "").trim() ||
                      !String(editRequirement.type || "").trim()
                    }
                  >
                    {isSavingRequirement ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Feedback Modal */}
          {editFeedbackModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Edit Feedback</h3>
                  <button
                    onClick={() => setEditFeedbackModal(null)}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isSavingFeedback}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">Message *</label>
                    <textarea
                      className="w-full min-h-[90px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                      value={editFeedbackMessage}
                      onChange={(e) => setEditFeedbackMessage(e.target.value)}
                      disabled={isSavingFeedback}
                    />
                  </div>
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => setEditFeedbackModal(null)}
                    disabled={isSavingFeedback}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isSavingFeedback || !editFeedbackMessage.trim()
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={saveFeedbackEdit}
                    disabled={isSavingFeedback || !editFeedbackMessage.trim()}
                  >
                    {isSavingFeedback ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Feedback Modal */}
          {isFeedbackModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Add Feedback</h3>
                  <button
                    onClick={() => setIsFeedbackModalOpen(false)}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isCreatingFeedback}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">Message *</label>
                    <textarea
                      placeholder="The client requested to change..."
                      className="w-full min-h-[80px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                      value={newFeedbackMessage}
                      onChange={(e) => setNewFeedbackMessage(e.target.value)}
                      disabled={isCreatingFeedback}
                    />
                  </div>
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => setIsFeedbackModalOpen(false)}
                    disabled={isCreatingFeedback}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isCreatingFeedback || !newFeedbackMessage.trim()
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={handleCreateFeedback}
                    disabled={isCreatingFeedback || !newFeedbackMessage.trim()}
                  >
                    {isCreatingFeedback ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Version History */}
          <div className="mt-4">
            <button
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors"
              onClick={async () => {
                const next = !showVersionHistory;
                setShowVersionHistory(next);
                if (next) await loadVersions();
              }}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>Version History</span>
              {showVersionHistory ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
            {showVersionHistory && (
              <div className="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
                {versionsError ? (
                  <div className="text-xs text-red-600">{versionsError}</div>
                ) : null}
                {isLoadingVersions ? (
                  <div className="text-xs text-gray-500">Loading versions...</div>
                ) : null}
                {!isLoadingVersions && (Array.isArray(scopeVersions) ? scopeVersions : []).length === 0 ? (
                  <div className="text-xs text-gray-500">No versions yet.</div>
                ) : null}
                {(Array.isArray(scopeVersions) ? scopeVersions : []).map((v) => (
                  <div key={v.id || v.version_number} className="relative">
                    <div className="absolute -left-[1.125rem] w-2 h-2 rounded-full bg-sm-primary border-2 border-white" />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-xs">
                          v{v.version_number ?? v.number ?? v.version ?? ""}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {formatDateOnly(v.created_at || v.created_on || v.createdAt || v.date || "")}
                          {v.created_by ? ` • ${v.created_by}` : ""}
                        </span>
                        <button
                          className="ml-2 h-6 w-6 flex items-center justify-center rounded-md text-sm-primary hover:bg-sm-primary/10 transition-colors"
                          onClick={() => {
                            setSelectedVersionId(v.id);
                            setIsVersionModalOpen(true);
                            loadVersionDetail(v.id);
                          }}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        {v.changes_description || v.changes || v.message || ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {isVersionModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-auto max-h-[90vh]">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">
                      Version Detail
                    </h3>
                    <button
                      onClick={() => setIsVersionModalOpen(false)}
                      className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                  <div className="p-4 space-y-3 text-xs">
                    {isLoadingVersionDetail ? (
                      <p className="text-sm text-gray-500">Loading...</p>
                    ) : versionDetailError ? (
                      <p className="text-sm text-red-600">{versionDetailError}</p>
                    ) : (
                      <>
                        <p className="text-[10px] text-gray-500">
                          Snapshot recorded at{" "}
                          {formatDateOnly(versionDetail?.created_at || versionDetail?.createdOn || "")}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          Changes: {versionDetail?.changes_description || "—"}
                        </p>
                        <div className="space-y-3">
                          {versionSnapshotCategories.length === 0 ? (
                            <p className="text-sm text-gray-500">No categories recorded in this version.</p>
                          ) : (
                            versionSnapshotCategories.map((category) => {
                              const requirements = category.requirements || [];
                              return (
                                <div
                                  key={`version-cat-${category.id ?? category.name}`}
                                  className="rounded-lg border border-gray-200 bg-white/80 p-3 space-y-2"
                                >
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-gray-700">
                                      {category.name || "Untitled category"}
                                    </p>
                                    <span className="text-[10px] text-gray-500">{requirements.length} fields</span>
                                  </div>
                                  {requirements.length === 0 ? (
                                    <p className="text-[10px] text-gray-500">No fields recorded.</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {requirements.map((req) => {
                                        const imagesCount =
                                          req.images_count ??
                                          (Array.isArray(req.images) ? req.images.length : null);
                                        return (
                                          <div
                                            key={`version-req-${req.id ?? req.field}`}
                                            className="border border-gray-100 rounded-md bg-gray-50 px-3 py-2"
                                          >
                                            <p className="text-[11px] font-semibold text-gray-700">
                                              {req.field || req.name || "Field"}
                                            </p>
                                            <div className="mt-1 flex flex-wrap gap-3 text-[10px] text-gray-500">
                                              <span>Type: {req.type || "—"}</span>
                                              <span>Value: {req.value ?? "—"}</span>
                                              {imagesCount > 0 ? (
                                                <span>
                                                  {imagesCount} image{imagesCount === 1 ? "" : "s"}
                                                </span>
                                              ) : null}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
